﻿import { FeatureCollectionWithProperties } from '@fe/maplibre-extensions';
import { UDViewGeojsonPath } from '../udView/udview';

export function checkGeojsonPathUnique(newPath: UDViewGeojsonPath, existingPaths: UDViewGeojsonPath[]): string | null {
    if (newPath.type === 'file') {
        const duplicate = existingPaths.some((p) => p.type === 'file' && p.fileHandle === newPath.fileHandle);
        return duplicate ? 'This file has already been added' : null;
    } else {
        const duplicate = existingPaths.some((p) => p.type === 'url' && p.url === newPath.url);
        return duplicate ? 'This URL has already been added' : null;
    }
}

export interface GeojsonValidationResult {
    valid: boolean;
    error?: string;
    data?: FeatureCollectionWithProperties;
}

export function validateGeojson(value: unknown): GeojsonValidationResult {
    if (typeof value !== 'object' || value === null) {
        return { valid: false, error: 'Not a valid JSON object' };
    }

    const obj = value as Record<string, unknown>;

    if (obj['type'] !== 'FeatureCollection') {
        return { valid: false, error: 'Root type must be "FeatureCollection"' };
    }

    if (!Array.isArray(obj['features'])) {
        return { valid: false, error: '"features" must be an array' };
    }

    return { valid: true, data: value as FeatureCollectionWithProperties };
}

export async function readAndValidateGeojsonFile(handle: FileSystemFileHandle): Promise<GeojsonValidationResult> {
    let text: string;
    try {
        const file = await handle.getFile();
        text = await file.text();
    } catch {
        return { valid: false, error: 'Failed to read file' };
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return { valid: false, error: 'File is not valid JSON' };
    }

    return validateGeojson(parsed);
}

export async function fetchAndValidateGeojsonUrl(url: string): Promise<GeojsonValidationResult> {
    let text: string;
    try {
        const response = await fetch(url);
        if (!response.ok) {
            return { valid: false, error: `HTTP ${response.status}: ${response.statusText}` };
        }
        text = await response.text();
    } catch {
        return { valid: false, error: 'Failed to fetch URL' };
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return { valid: false, error: 'Response is not valid JSON' };
    }

    return validateGeojson(parsed);
}
